import {
  IMarketingSpendView,
  marketingMetricView,
  MARKETING_CHANNELS,
  MARKETING_NONE_KEY,
} from './marketingModel';
import { IMarketingMatrixCell, IMarketingMetrics } from './types';

/**
 * ТЗ 0088, проход 29.09 — матрица «канал × курс» на «Каналах» (запрос
 * `marketingMatrix`). Чистая часть: какие показатели переключаются, порядок
 * строк и столбцов, значение ячейки (CPL — с «за собранные дни», как везде) и
 * тон по величине. Без React и i18n.
 */

/** Показатели переключателя — в порядке кнопок. */
export const MARKETING_MATRIX_METRICS = [
  'sales',
  'revenuePln',
  'crmLeads',
  'cplPln',
] as const;

export type MarketingMatrixMetric = typeof MARKETING_MATRIX_METRICS[number];

export const MARKETING_MATRIX_DEFAULT_METRIC: MarketingMatrixMetric = 'sales';

export const isMarketingMatrixMetric = (
  value: string | null | undefined,
): value is MarketingMatrixMetric =>
  Boolean(value) &&
  (MARKETING_MATRIX_METRICS as readonly string[]).includes(value as string);

/**
 * Ступеней тона. 0 — значения нет или оно ноль; 1…4 — доля от НАИБОЛЬШЕГО
 * значения во всей таблице (не в строке): так сравниваются и каналы между
 * собой, и курсы внутри канала — самая насыщенная клетка одна на таблицу.
 */
export const MARKETING_MATRIX_TONE_STEPS = 4;

export const marketingMatrixCellKey = (row: string, col: string): string =>
  `${row}|${col}`;

export const marketingMatrixTone = (
  value: number | null,
  max: number,
): number => {
  if (value === null || value <= 0 || max <= 0) return 0;
  return Math.min(
    MARKETING_MATRIX_TONE_STEPS,
    Math.max(1, Math.ceil((value / max) * MARKETING_MATRIX_TONE_STEPS)),
  );
};

export interface IMarketingMatrixAxis {
  key: string;
  /** Подпись сервера (у курса «не определён» — пусто: подпись даёт экран). */
  label: string;
}

export interface IMarketingMatrixViewCell {
  rowKey: string;
  colKey: string;
  metrics: IMarketingMetrics;
  /** Значение выбранного показателя; CPL — основной или за собранные дни. */
  view: IMarketingSpendView;
  /** 0…`MARKETING_MATRIX_TONE_STEPS`. */
  tone: number;
}

export interface IMarketingMatrixView {
  rows: IMarketingMatrixAxis[];
  cols: IMarketingMatrixAxis[];
  /** По `marketingMatrixCellKey(row, col)`; нет ключа — ячейка пустая. */
  cells: Record<string, IMarketingMatrixViewCell>;
  /** Наибольшее значение таблицы — основа тона; 0 — значений нет. */
  max: number;
}

const channelOrder = (key: string): number => {
  const index = (MARKETING_CHANNELS as string[]).indexOf(key);
  return index < 0 ? MARKETING_CHANNELS.length : index;
};

/**
 * Таблица из плоского списка непустых ячеек. Строки и столбцы — по CRM-лидам
 * по убыванию (порядок не прыгает при смене показателя), при равенстве —
 * порядок словаря каналов / имя курса; «курс не определён» — последним
 * столбцом.
 */
export const marketingMatrixView = (
  cells: ReadonlyArray<IMarketingMatrixCell>,
  metric: MarketingMatrixMetric,
): IMarketingMatrixView => {
  const rowLeads = new Map<string, number>();
  const colLeads = new Map<string, number>();
  const rowLabels = new Map<string, string>();
  const colLabels = new Map<string, string>();
  const byKey: Record<string, IMarketingMatrixViewCell> = {};
  let max = 0;
  cells.forEach((cell) => {
    rowLeads.set(
      cell.rowKey,
      (rowLeads.get(cell.rowKey) ?? 0) + cell.metrics.crmLeads,
    );
    colLeads.set(
      cell.colKey,
      (colLeads.get(cell.colKey) ?? 0) + cell.metrics.crmLeads,
    );
    rowLabels.set(cell.rowKey, cell.rowLabel);
    colLabels.set(cell.colKey, cell.colLabel);
    const view = marketingMetricView(cell.metrics, metric);
    if (view.value !== null) max = Math.max(max, view.value);
    byKey[marketingMatrixCellKey(cell.rowKey, cell.colKey)] = {
      rowKey: cell.rowKey,
      colKey: cell.colKey,
      metrics: cell.metrics,
      view,
      tone: 0,
    };
  });
  Object.values(byKey).forEach((cell) => {
    // eslint-disable-next-line no-param-reassign
    cell.tone = marketingMatrixTone(cell.view.value, max);
  });
  const rows = Array.from(rowLeads.keys())
    .sort(
      (a, b) =>
        (rowLeads.get(b) ?? 0) - (rowLeads.get(a) ?? 0) ||
        channelOrder(a) - channelOrder(b) ||
        a.localeCompare(b),
    )
    .map((key) => ({ key, label: rowLabels.get(key) ?? '' }));
  const cols = Array.from(colLeads.keys())
    .sort(
      (a, b) =>
        Number(a === MARKETING_NONE_KEY) - Number(b === MARKETING_NONE_KEY) ||
        (colLeads.get(b) ?? 0) - (colLeads.get(a) ?? 0) ||
        (colLabels.get(a) ?? a).localeCompare(colLabels.get(b) ?? b),
    )
    .map((key) => ({ key, label: colLabels.get(key) ?? '' }));
  return { rows, cols, cells: byKey, max };
};
